import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createCompany,
  deleteCompany,
  getCompanies,
  getCompaniesExist,
  getDefaultCompany,
  updateCompany,
  type CompanyPayload,
} from "../api/companies";

export const useCompanies = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ["companies"],
    queryFn: getCompanies,
    staleTime: 1000 * 60 * 30,
    enabled: options?.enabled ?? true,
  });
};

export const useDefaultCompany = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ["companies", "default"],
    queryFn: getDefaultCompany,
    // Винаги refetch при mount — InvoiceCreateModal зависи от
    // `last_invoice_number` за да изчисли „следващ номер +1". Без този
    // флаг последно използвания номер можеше да остане в кеша, ако
    // mutation invalidate не успее да обнови преди модалът да се отвори.
    staleTime: 0,
    refetchOnMount: "always",
    retry: false,
    enabled: options?.enabled ?? true,
  });
};

// Ключът е под „companies" — създаване/изтриване на фирма го обновява заедно
// със списъка.
export const useCompaniesExist = () => {
  return useQuery({
    queryKey: ["companies", "exists"],
    queryFn: getCompaniesExist,
    staleTime: 1000 * 60 * 30,
    retry: false,
  });
};

export const useCreateCompany = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CompanyPayload) => createCompany(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["companies"] });
    },
  });
};

export const useUpdateCompany = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: CompanyPayload }) =>
      updateCompany(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["companies"] });
    },
  });
};

export const useDeleteCompany = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteCompany(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["companies"] });
    },
  });
};

